// frame: GKK2D @ ui/dogdisk-user.pen
// spec: docs/ui/user-page-flows.md §2.6 GKK2D 页面-客服工单
//
// 我的工单列表;入口:QQKOK 客服工单 row (xzL0s) / EKOSx menuItem2 (cTzdC,
// 未登录跳 login)。
// 数据源:useMyTickets() 走 /gapi/user/tickets?scope=mine
// (canonical R-06,见 _lib/messaging-api.ts)。
// 详情入口:/tickets/:id(已实装 ticket-detail.tsx);新建:/tickets/new。

import { useNavigate } from 'react-router-dom'
import { Spinner, cn } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen, TopBar } from '../components/mobile-screen'
import { useMyTickets } from '../_lib/messaging-api'
import type { Ticket } from '../_lib/messaging-api'

const STATE_LABEL: Record<string, string> = {
  open: '待处理',
  in_progress: '进行中',
  pending: '待回复',
  resolved: '已解决',
  closed: '已关闭',
}

const STATE_CLASS: Record<string, string> = {
  open: 'bg-amber-50 text-amber-600',
  in_progress: 'bg-emerald-50 text-emerald-600',
  pending: 'bg-amber-50 text-amber-600',
  resolved: 'bg-sky-50 text-sky-600',
  closed: 'bg-gray-100 text-gray-500',
}

const TYPE_LABEL: Record<string, string> = {
  account: '账号',
  payment: '支付',
  file: '文件',
  share: '分享',
  other: '其他',
}

export function TicketsPage() {
  const navigate = useNavigate()
  const { data, isLoading, error } = useMyTickets()
  const tickets: Ticket[] = data?.tickets ?? []

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <div data-testid="GKK2D" className="flex flex-1 flex-col">
          <TopBar
            title="客服工单"
            fallback="/profile"
            right={
              <button
                type="button"
                data-testid="HNSJb"
                aria-label="新建工单"
                onClick={() => navigate('/tickets/new')}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-[#19caad] text-white"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
            }
          />

          <div className="flex-1 px-6 pb-24 pt-2">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Spinner className="text-gray-400" />
              </div>
            ) : error ? (
              <div className="py-12 text-center text-[13px] text-[#FF6B6B]">
                加载失败,请稍后重试
              </div>
            ) : tickets.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-16 text-[13px] text-[#9CA3AF]">
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
                </svg>
                <p>暂无工单</p>
                <button
                  type="button"
                  onClick={() => navigate('/tickets/new')}
                  className="mt-1 rounded-lg bg-[#19caad] px-4 py-2 text-[13px] font-semibold text-white"
                >
                  新建工单
                </button>
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {tickets.map((t, i) => {
                  // ticketItem1~5 对应 .pen htZRV/rpNWp/mJM9R/iiyki/im6VH
                  const ids = ['htZRV', 'rpNWp', 'mJM9R', 'iiyki', 'im6VH']
                  const itemId = ids[i] ?? `ticket-${i}`
                  return (
                    <li key={t.id}>
                      <button
                        type="button"
                        data-testid={itemId}
                        onClick={() => navigate(`/tickets/${t.id}`)}
                        className="flex w-full flex-col gap-1 rounded-xl bg-white p-3 text-left active:bg-gray-50"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[11px] text-[#9CA3AF]">
                            # {t.ticket_number || t.id.slice(0, 8)}
                          </span>
                          <span
                            className={cn(
                              'rounded-md px-2 py-0.5 text-[11px] font-medium',
                              STATE_CLASS[t.state] ?? 'bg-gray-100 text-gray-500',
                            )}
                          >
                            {STATE_LABEL[t.state] ?? t.state}
                          </span>
                        </div>
                        <p className="truncate text-[14px] font-medium text-[#1A1A1A]">
                          {t.title}
                        </p>
                        <div className="flex items-center justify-between text-[11px] text-[#9CA3AF]">
                          <span>{TYPE_LABEL[t.type] ?? t.type}</span>
                          <span>{t.created_at.slice(0, 10)}</span>
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      </MobileScreen>
    </AppLayout>
  )
}
